import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, Package, Search, Ship } from "lucide-react";
import { toast } from "sonner";
import { API, downloadExcel, imgUrl } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function ReorderReport() {
  const [data, setData] = useState(null);
  const [leadEdits, setLeadEdits] = useState({});
  const [savingLead, setSavingLead] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState({});
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await API.get("/inventory/reorder-report");
      setData(r.data);
      const le = {};
      r.data.items.forEach((i) => {
        le[i.product_id] = i.lead_time_days || "";
      });
      setLeadEdits(le);
    } catch {
      toast.error("Could not load reorder report");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveLead = async (pid) => {
    if (savingLead) return;
    setSavingLead(pid);
    try {
      await API.patch(`/products/${pid}/lead-time`, { lead_time_days: Number(leadEdits[pid]) || 0 });
      toast.success("Lead time updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save lead time");
    } finally {
      setSavingLead("");
    }
  };

  if (!data) return <div className="text-sm text-slate-500" data-testid="reorder-loading">Loading reorder plan...</div>;

  const categories = [...new Set(data.items.map((i) => i.category).filter(Boolean))].sort();
  const q = search.trim().toLowerCase();
  const filtered = data.items.filter(
    (it) =>
      (category === "all" || it.category === category) &&
      (!q || it.name.toLowerCase().includes(q) || (it.sku || "").toLowerCase().includes(q))
  );
  const allSelected = filtered.length > 0 && filtered.every((it) => selected[it.product_id]);
  const selectedCount = filtered.filter((it) => selected[it.product_id]).length;

  const toggle = (id) =>
    setSelected((s) => {
      const n = { ...s };
      if (n[id]) delete n[id];
      else n[id] = true;
      return n;
    });

  const toggleAll = () => {
    const n = { ...selected };
    if (allSelected) filtered.forEach((it) => delete n[it.product_id]);
    else filtered.forEach((it) => (n[it.product_id] = true));
    setSelected(n);
  };

  const exportExcel = async () => {
    if (exporting) return;
    const ids = filtered.filter((it) => selected[it.product_id]).map((it) => it.product_id);
    const list = ids.length ? ids : filtered.map((it) => it.product_id);
    if (!list.length) {
      toast.error("Nothing to export");
      return;
    }
    setExporting(true);
    try {
      await downloadExcel(`/inventory/reorder-report/export?product_ids=${list.join(",")}`, "next-container-plan.xlsx");
      toast.success(`Exported ${list.length} item${list.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const daysBadge = (it) => {
    if (it.days_of_stock == null) {
      return <Badge variant="outline" className="text-slate-500" data-testid={`reorder-days-${it.product_id}`}>No consumption yet</Badge>;
    }
    const cls =
      it.days_of_stock <= it.effective_lead_time
        ? "bg-rose-100 text-rose-700 hover:bg-rose-100"
        : it.days_of_stock <= it.effective_lead_time * 1.5
          ? "bg-amber-100 text-amber-700 hover:bg-amber-100"
          : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100";
    return <Badge className={cls} data-testid={`reorder-days-${it.product_id}`}>{it.days_of_stock} days</Badge>;
  };

  const ctn = (it, qty) => {
    const ppc = it.pcs_per_ctn || 0;
    return ppc > 0 && qty > 0 ? `≈ ${Math.ceil(qty / ppc)} ctn` : "";
  };

  return (
    <div className="space-y-5" data-testid="reorder-report-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2.5 font-heading text-3xl font-bold text-slate-900">
            <Ship className="h-7 w-7 text-indigo-600" /> Next Container — Reorder Plan
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Consumption = units transferred from Company 1 warehouses to {data.office_warehouse || "Office Stock"} · stock shown excludes {data.office_warehouse || "Office Stock"} and B2B
          </p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={exportExcel} disabled={exporting} data-testid="reorder-export-button">
          {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
          {selectedCount > 0 ? `Export ${selectedCount} Selected` : "Export All Shown"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="reorder-filters">
        <div className="relative min-w-52 flex-1">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search item or SKU..." className="bg-white pl-8" data-testid="reorder-search-input" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-48 bg-white" data-testid="reorder-category-select">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="reorder-category-all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c} data-testid={`reorder-category-${c}`}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(search || category !== "all") && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setCategory("all"); }} data-testid="reorder-clear-filters">
            Clear filters
          </Button>
        )}
      </div>

      {data.items.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <EmptyState title="No products yet" hint="Add products in Inventory to see reorder suggestions." />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <EmptyState title="No items match" hint="Try a different search or category." />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all shown" data-testid="reorder-select-all" />
                </TableHead>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Warehouse Stock</TableHead>
                <TableHead className="text-right">Avg Daily Use</TableHead>
                <TableHead className="text-right">Days Left</TableHead>
                <TableHead className="text-right">Lead Time (days)</TableHead>
                <TableHead className="text-right">Plan Qty (rate × lead)</TableHead>
                <TableHead className="text-right">Order Qty (shortfall)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="reorder-table">
              {filtered.map((it) => (
                <TableRow key={it.product_id} className={it.avg_daily_consumption <= 0 ? "opacity-70" : it.order_now ? "bg-rose-50/60 hover:bg-rose-50" : ""} data-testid={`reorder-row-${it.product_id}`}>
                  <TableCell>
                    <Checkbox checked={!!selected[it.product_id]} onCheckedChange={() => toggle(it.product_id)} aria-label={`Select ${it.name}`} data-testid={`reorder-select-${it.product_id}`} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {it.image_url ? (
                        <img src={imgUrl(it.image_url)} alt={it.name} className="h-9 w-9 rounded-md border border-slate-200 object-cover" />
                      ) : (
                        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-400"><Package className="h-4 w-4" /></div>
                      )}
                      <div>
                        <Link to={`/inventory/product/${it.product_id}`} className="font-medium text-indigo-700 hover:underline" data-testid={`reorder-product-${it.product_id}`}>{it.name}</Link>
                        <div className="text-xs text-slate-400">
                          {it.sku ? `${it.sku} · ` : ""}{it.category ? `${it.category} · ` : ""}{it.unit}{(it.pcs_per_ctn || 0) > 0 ? ` · 1 ctn = ${it.pcs_per_ctn} pcs` : ""}
                        </div>
                      </div>
                      {it.order_now && (
                        <Badge className="ml-1 bg-rose-600 text-white hover:bg-rose-600" data-testid={`reorder-urgent-${it.product_id}`}>Order now</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium" data-testid={`reorder-stock-${it.product_id}`}>{it.warehouse_stock} {it.unit}</TableCell>
                  <TableCell className="text-right" data-testid={`reorder-avg-${it.product_id}`}>
                    {it.avg_daily_consumption > 0 ? `${it.avg_daily_consumption}/day` : "—"}
                  </TableCell>
                  <TableCell className="text-right">{daysBadge(it)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Input
                        type="number"
                        min="0"
                        value={leadEdits[it.product_id] ?? ""}
                        placeholder={String(data.default_lead_time)}
                        onChange={(e) => setLeadEdits((le) => ({ ...le, [it.product_id]: e.target.value }))}
                        onBlur={() => Number(leadEdits[it.product_id] || 0) !== it.lead_time_days && saveLead(it.product_id)}
                        onKeyDown={(e) => e.key === "Enter" && Number(leadEdits[it.product_id] || 0) !== it.lead_time_days && saveLead(it.product_id)}
                        className="h-8 w-20 text-right"
                        data-testid={`reorder-lead-input-${it.product_id}`}
                      />
                      {savingLead === it.product_id && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-400" data-testid={`reorder-lead-hint-${it.product_id}`}>
                      {it.lead_time_is_default ? `${data.default_lead_time} (default)` : `${it.lead_time_days} (set)`}
                    </div>
                  </TableCell>
                  <TableCell className="text-right" data-testid={`reorder-plan-qty-${it.product_id}`}>
                    {it.plan_qty > 0 ? (
                      <>
                        <span className="font-medium">{it.plan_qty} {it.unit}</span>
                        {ctn(it, it.plan_qty) && <div className="text-[11px] text-slate-400">{ctn(it, it.plan_qty)}</div>}
                      </>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right" data-testid={`reorder-order-qty-${it.product_id}`}>
                    {it.order_qty > 0 ? (
                      <>
                        <span className="font-bold text-rose-700">{it.order_qty} {it.unit}</span>
                        {ctn(it, it.order_qty) && <div className="text-[11px] font-medium text-rose-500">{ctn(it, it.order_qty)}</div>}
                      </>
                    ) : it.avg_daily_consumption > 0 ? (
                      <span className="text-xs font-medium text-emerald-600">Covered</span>
                    ) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-xs text-slate-400" data-testid="reorder-footnote">
        Sorted by urgency — fewest days of stock first; items never moved to {data.office_warehouse || "Office Stock"} appear at the bottom. Lead time saves when you click away (blank = {data.default_lead_time}-day default). Order Qty = Plan Qty − warehouse stock (0 when covered). Excel export includes item images and covers your selection, or all shown items when nothing is selected.
      </p>
    </div>
  );
}
